/**
 * ActivationsMenuValidation / ValidationRow - dynamic validation row (ITRTL-720)
 *
 * Cloned from MenuValidation/ValidationRow. Differences:
 *  - Validation cells are rendered dynamically from `validationTypes`
 *    (the aggregator's applicableValidations) rather than fixed columns.
 *  - Handles the new MENU and CATEGORY entity types: no category cell value,
 *    no per-item preview/edit (View/Edit guarded to ITEM/MODIFIER/MODIFIER_GROUP).
 */

/* eslint-disable react/prop-types */
import React, { useState, useRef, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import queryString from "query-string";
import languageCodes from "../../utils/langCodes";
import {
  VALIDATION_TYPE_DISPLAY,
  VALIDATION_STATUS,
  VALIDATION_TYPES,
  VALIDATION_FIELD_TYPES
} from "../../utils/enums";

// URL path templates for Grubcenter admin edit pages
const MENU_ITEM_PATH_TEMPLATE = "/menu-management/menu-items";
const MODIFIER_GROUP_PATH_TEMPLATE = "/menu-management/modifier-groups";

// CSS `min-width` of the dropdown menu (keep in sync with .ls-validation-action-dropdown-menu).
const DROPDOWN_MENU_MIN_WIDTH = 140;

/**
 * Resolve the Grubcenter base URL: admin GC when `?isAdminGC=true`, otherwise the standard GC.
 */
const getGrubcenterBaseUrl = (search) => {
  const params = queryString.parse(search || "");
  return params.isAdminGC === "true"
    ? process.env.REACT_APP_ADMIN_GC_URL || null
    : process.env.REACT_APP_GC_URL || null;
};

/**
 * Action dropdown (View / Edit) for a previewable row. Hoisted to module scope so it isn't
 * re-created on every ValidationRow render.
 */
const ActionDropdown = ({ validationResult, type, onView }) => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);

  const gcUrl = useMemo(() => getGrubcenterBaseUrl(location.search), [location.search]);
  const canEdit = !!gcUrl && !!validationResult.recipeId;

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // ESC to close
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscKey);
    }
    return () => document.removeEventListener("keydown", handleEscKey);
  }, [isOpen]);

  // Close on scroll / resize (fixed positioning)
  useEffect(() => {
    const close = () => setIsOpen(false);
    if (isOpen) {
      window.addEventListener("scroll", close, true);
      window.addEventListener("resize", close);
    }
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [isOpen]);

  const toggleDropdown = (event) => {
    event.stopPropagation();
    if (!isOpen && dropdownRef.current) {
      const buttonRect = dropdownRef.current.getBoundingClientRect();
      setMenuPosition({ left: buttonRect.right - DROPDOWN_MENU_MIN_WIDTH, top: -9999 });
    }
    setIsOpen(!isOpen);
  };

  // Measure and position the menu after it renders
  useEffect(() => {
    if (isOpen && menuRef.current && dropdownRef.current) {
      requestAnimationFrame(() => {
        if (menuRef.current && dropdownRef.current) {
          const menuRect = menuRef.current.getBoundingClientRect();
          const buttonRect = dropdownRef.current.getBoundingClientRect();
          const spaceBelow = window.innerHeight - buttonRect.bottom;
          const shouldOpenUpward = spaceBelow < menuRect.height;
          setOpenUpward(shouldOpenUpward);

          const gap = 4;
          setMenuPosition({
            left: buttonRect.right - menuRect.width,
            top: shouldOpenUpward ? buttonRect.top - menuRect.height - gap : buttonRect.bottom + gap
          });
        }
      });
    }
  }, [isOpen]);

  const handleDropdownViewClick = (event) => {
    event.stopPropagation();
    onView();
    setIsOpen(false);
  };

  const handleEditClick = (event) => {
    event.stopPropagation();
    if (!canEdit) return;

    const { recipeId } = validationResult;
    const path = type === VALIDATION_TYPES.ITEM ? MENU_ITEM_PATH_TEMPLATE : MODIFIER_GROUP_PATH_TEMPLATE;
    window.open(`${gcUrl}${path}/${encodeURIComponent(recipeId)}`, "_blank", "noopener,noreferrer");
    setIsOpen(false);
  };

  return (
    <div className="ls-validation-action-dropdown" ref={dropdownRef}>
      <button
        className="ls-validation-action-dropdown-button"
        onClick={toggleDropdown}
        aria-label="Actions"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        type="button"
      >
        ⋮
      </button>

      {isOpen && (
        <div
          ref={menuRef}
          className={`ls-validation-action-dropdown-menu ${openUpward ? "ls-validation-action-dropdown-menu--upward" : ""}`}
          role="menu"
          style={{ top: `${menuPosition.top}px`, left: `${menuPosition.left}px` }}
        >
          <button
            className="ls-validation-action-dropdown-item"
            onClick={handleDropdownViewClick}
            role="menuitem"
            type="button"
          >
            <span className="ls-action-icon">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 8s3-5 7-5 7 5 7 5-3 5-7 5-7-5-7-5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
            <span>View</span>
          </button>

          <button
            className="ls-validation-action-dropdown-item"
            onClick={handleEditClick}
            disabled={!canEdit}
            role="menuitem"
            type="button"
            title={!canEdit ? "Edit unavailable - configuration required" : "Edit item in Grubcenter"}
          >
            <span className="ls-action-icon">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.333 2A1.886 1.886 0 0114 4.667l-9 9-3.667.666.667-3.666 9-9z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
            <span>Edit</span>
          </button>
        </div>
      )}
    </div>
  );
};

const ValidationRow = ({ validationResult, validationTypes, metadata, onOpenModal, onOpenPanel }) => {
  const { type, name, validation } = validationResult;

  const getLanguageNames = (codes) => {
    if (!codes || codes.length === 0) return [];
    return codes.map((code) => languageCodes[code.toLowerCase()] || code);
  };

  const getTypeDisplay = () => VALIDATION_TYPE_DISPLAY[type] || type;

  /**
   * Render a standard validation indicator with optional missing-language tooltip.
   */
  const renderValidationIndicator = (validationField) => {
    if (!validationField || validationField.status === VALIDATION_STATUS.NOT_APPLICABLE) {
      return <span className="ls-validation-indicator ls-validation-indicator--na">N/A</span>;
    }

    if (validationField.status === VALIDATION_STATUS.VALID) {
      return (
        <span className="ls-validation-indicator-circle ls-validation-indicator-circle--valid">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M13.3334 4L6.00008 11.3333L2.66675 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      );
    }

    const { missingLanguages } = validationField;
    const hasMissingLanguages = missingLanguages && missingLanguages.length > 0;

    return (
      <div className="ls-validation-indicator-wrapper">
        <span className="ls-validation-indicator-circle ls-validation-indicator-circle--error">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
        {hasMissingLanguages && (
          <div className="ls-validation-tooltip">
            Missing languages: {getLanguageNames(missingLanguages).join(", ")}
          </div>
        )}
      </div>
    );
  };

  /**
   * Format a minor-units price using the response currency metadata.
   */
  const formatPrice = (priceValue) => {
    if (typeof priceValue !== "number") return "—";
    const precision = metadata?.currencyPrecision ?? 2;
    const currencyCode = metadata?.currencyCode;
    const numericValue = priceValue / Math.pow(10, precision);
    if (currencyCode) {
      try {
        return new Intl.NumberFormat(undefined, { style: "currency", currency: currencyCode }).format(numericValue);
      } catch (e) {
        return numericValue.toFixed(precision);
      }
    }
    return numericValue.toFixed(precision);
  };

  /**
   * Render the duplicate-item-price indicator with a detailed tooltip.
   */
  const renderDuplicateIndicator = (validationField) => {
    if (!validationField || validationField.status === VALIDATION_STATUS.NOT_APPLICABLE) {
      return <span className="ls-validation-indicator ls-validation-indicator--na">N/A</span>;
    }

    if (validationField.status === VALIDATION_STATUS.VALID) {
      return (
        <span className="ls-validation-indicator-circle ls-validation-indicator-circle--valid">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M13.3334 4L6.00008 11.3333L2.66675 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      );
    }

    const { value, duplicateCount, conflictingPrices } = validationField;
    const formattedPrices = conflictingPrices ? conflictingPrices.map((p) => formatPrice(p)).join(", ") : "";

    return (
      <div className="ls-validation-indicator-wrapper">
        <span className="ls-validation-indicator-circle ls-validation-indicator-circle--error">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
        <div className="ls-validation-tooltip">
          <div>Duplicate: {value}</div>
          {formattedPrices && <div>Prices: {formattedPrices}</div>}
          {duplicateCount && <div>Items Count: {duplicateCount}</div>}
        </div>
      </div>
    );
  };

  /**
   * Render the cell for a single validation type, dispatching to the right indicator.
   */
  const renderCell = (validationType) => {
    const field = validation ? validation[validationType] : null;
    if (validationType === VALIDATION_FIELD_TYPES.DUPLICATE_ITEM_NAME_PRICE) {
      return renderDuplicateIndicator(field);
    }
    return renderValidationIndicator(field);
  };

  // View/preview is only meaningful for menu items and modifiers/groups
  const isPreviewable =
    type === VALIDATION_TYPES.ITEM ||
    type === VALIDATION_TYPES.MODIFIER_GROUP ||
    type === VALIDATION_TYPES.MODIFIER;

  const handleViewClick = () => {
    if (type === VALIDATION_TYPES.ITEM) {
      onOpenPanel(validationResult);
    } else if (type === VALIDATION_TYPES.MODIFIER_GROUP || type === VALIDATION_TYPES.MODIFIER) {
      onOpenModal(validationResult);
    }
  };

  return (
    <tr
      className="ls-validation-table-row"
      onClick={isPreviewable ? handleViewClick : undefined}
      role={isPreviewable ? "button" : undefined}
      tabIndex={isPreviewable ? 0 : undefined}
      onKeyDown={(e) => {
        if (isPreviewable && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          handleViewClick();
        }
      }}
    >
      <td className="ls-validation-table-cell">{getTypeDisplay()}</td>
      <td className="ls-validation-table-cell ls-validation-table-cell--name">{name}</td>
      <td className="ls-validation-table-cell ls-validation-table-cell--category">
        {type === VALIDATION_TYPES.ITEM ? (validationResult.categoryName || "-") : "-"}
      </td>
      {validationTypes.map((validationType) => (
        <td
          key={validationType}
          className="ls-validation-table-cell ls-validation-table-cell--indicator"
        >
          {renderCell(validationType)}
        </td>
      ))}
      <td className="ls-validation-table-cell ls-validation-table-cell--action">
        {isPreviewable && (
          <ActionDropdown validationResult={validationResult} type={type} onView={handleViewClick} />
        )}
      </td>
    </tr>
  );
};

export default ValidationRow;
